import type { AppLocale } from "./types";
import type { LandingFaqItem } from "./landingTypes";

type FaqBundle = { faqTitle: string; faqItems: LandingFaqItem[] };

function fb(title: string, items: LandingFaqItem[]): FaqBundle {
  return { faqTitle: title, faqItems: items };
}

const en = fb("Frequently asked questions", [
  {
    q: "What is Wedy.Me?",
    a: "Wedy.Me lets you create your wedding invitation on your phone and share it with a single link and QR code. Guests open the invite in the browser; you track responses in one place.",
  },
  {
    q: "Do guests need to download an app?",
    a: "No. When they tap the invitation link or QR code, the page opens on a phone or computer. Guests do not need to install a separate app.",
  },
  {
    q: "How do I share my invitation?",
    a: "From the app you can copy the shareable link or save the QR code and send it by message, email, or social media.",
  },
  {
    q: "What appears on the invitation page?",
    a: "Typically the couple’s names, wedding date and time, venue with a map link, a short invitation message, and your chosen image. If you use Pro features (for example schedule lines, meal preference, or advanced RSVP), those appear on the same page too.",
  },
  {
    q: "Can I change the date, time, or venue later?",
    a: "Yes. When you update details in the app, the invitation page is published with the new information. Your shared link usually stays the same, so you don’t need to resend a new address—but it’s still a good idea to message guests about important changes.",
  },
  {
    q: "What is RSVP, and what does a guest do?",
    a: "RSVP means “please reply.” The guest tells you whether they are coming and, if you ask, how many people they are bringing. After they submit the form, the response is saved and you can follow along in your list.",
  },
  {
    q: "Where do I see the guest list and responses?",
    a: "You can view responses in your invitation’s management area inside the app.",
  },
  {
    q: "Is Wedy.Me only available on iPhone?",
    a: "Creating and editing your invitation currently uses the Wedy.Me iOS (iPhone) app. Guests can open the published invitation page from any device.",
  },
  {
    q: "Are my data and guest information secure?",
    a: "Connections are served over secure HTTPS. Information guests provide is used to register their reply for your event; see our Privacy Policy page for details.",
  },
  {
    q: "Who can access my invitation link?",
    a: "Anyone who has the link or QR code can open the invitation page. Share it only with people you want to invite. Avoid posting highly sensitive details or sharing the link publicly.",
  },
  {
    q: "What’s included in the free plan, and what does Pro add?",
    a: "The free plan usually includes a limited number of invitations and a basic invitation page. Pro can add benefits such as unlimited (or higher-limit) invitations, turning RSVP on or off, a guest list, approve/decline, and instant notifications—exactly as offered in the current app.",
  },
  {
    q: "How do I cancel my subscription?",
    a: "Subscriptions are managed through the App Store: go to Settings > Apple ID > Subscriptions to cancel or turn off renewal for your Wedy.Me subscription. Payments and refunds follow Apple’s rules.",
  },
]);

const tr = fb("Sıkça sorulan sorular", [
  {
    q: "Wedy.Me nedir?",
    a: "Wedy.Me, düğün davetiyenizi telefonunuzdan oluşturup tek bir link ve QR kod ile paylaşmanızı sağlar. Misafirleriniz davetiyeyi tarayıcıda açar; siz de katılımları tek yerden takip edebilirsiniz.",
  },
  {
    q: "Misafirler uygulama indirmek zorunda mı?",
    a: "Hayır. Davet linkine veya QR koda tıkladıklarında sayfa telefon veya bilgisayarda açılır. Misafirler için ayrı bir uygulama kurulumu gerekmez.",
  },
  {
    q: "Davetiyemi nasıl paylaşırım?",
    a: "Uygulama içinden paylaşılabilir bağlantıyı kopyalayabilir veya QR kodu indirip mesaj, e-posta veya sosyal medyada gönderebilirsiniz.",
  },
  {
    q: "Davet sayfasında neler yer alıyor?",
    a: "Gelin ve damat isimleri, düğün tarihi ve saati, mekân ve haritaya giden bağlantı, kısa davet metni ve seçtiğiniz görsel gibi bileşenler bulunur. Pro özelliklerinizde (örneğin program satırları, yemek tercihi, gelişmiş RSVP) varsa bunlar da aynı sayfada toplanır.",
  },
  {
    q: "Tarih, saat veya mekânı sonra değiştirebilir miyim?",
    a: "Evet. Bilgileri uygulama üzerinden güncellediğinizde davet sayfası yeni haliyle yayınlanır. Paylaştığınız link aynı kaldığı için misafirlere yeni adres göndermeniz gerekmez; yine de önemli değişikliklerde kısa bir hatırlatma mesajı atmanız iyi olur.",
  },
  {
    q: "RSVP nedir, misafir ne yapar?",
    a: "RSVP (“lütfen yanıtlayınız”), misafirin katılıp katılmayacağını ve varsa yanlarında kaç kişi olduğunu size bildirmesidir. Formu doldurup gönderdiğinde yanıt kayda geçer; siz de listeden takip edebilirsiniz.",
  },
  {
    q: "Katılım listesini ve yanıtları nereden görürüm?",
    a: "Yanıtları uygulama içindeki davetinizin yönetim alanından görüntüleyebilirsiniz.",
  },
  {
    q: "Sadece iPhone’da mı kullanılabilir?",
    a: "Davetiyeyi oluşturmak ve düzenlemek için Wedy.Me şu an iOS (iPhone) uygulamasıdır. Oluşan davet sayfasına misafirler her cihazdan erişebilir.",
  },
  {
    q: "Verilerim ve misafir bilgileri güvende mi?",
    a: "Bağlantılar güvenli (HTTPS) ile sunulur. Misafirlerin paylaştığı bilgiler davetinize yönelik kayıt için kullanılır; ayrıntılar için Gizlilik Politikası sayfamıza bakabilirsiniz.",
  },
  {
    q: "Davet linkim kime açık?",
    a: "Linki veya QR’ı bilen herkes davet sayfasını açabilir. Bu nedenle bağlantıyı yalnızca davet etmek istediğiniz kişilerle paylaşmanızı öneririz. Çok hassas bilgi paylaşmayın veya herkese açık yerlerde linki yaymayın.",
  },
  {
    q: "Ücretsiz planda neler var, Pro ne ekliyor?",
    a: "Ücretsiz planda genellikle sınırlı sayıda davetiye oluşturma ve temel davet sayfası sunulur. Pro ile örneğin sınırsız (veya daha yüksek limit) davetiye, RSVP’yi açıp kapatma, misafir listesi, onay/red ve anlık bildirimler gibi özellikler eklenir.",
  },
  {
    q: "Aboneliği nasıl iptal ederim?",
    a: "Abonelikler App Store üzerinden yönetilir: Ayarlar > Apple Kimliği > Abonelikler yolunu kullanarak Wedy.Me aboneliğinizi iptal edebilir veya yenilemeyi kapatabilirsiniz. Ödemeler ve iadeler Apple kurallarına tabidir.",
  },
]);

// —— Aşağıdaki diller İngilizce içerikten yerelleştirilmiştir —— //

const de = fb("Häufig gestellte Fragen", [
  {
    q: "Was ist Wedy.Me?",
    a: "Mit Wedy.Me erstellen Sie Ihre Hochzeitseinladung auf dem Smartphone und teilen sie mit einem einzigen Link und QR-Code. Gäste öffnen die Einladung im Browser; Sie behalten die Antworten an einem Ort im Blick.",
  },
  {
    q: "Müssen Gäste eine App herunterladen?",
    a: "Nein. Wenn sie auf den Einladungslink oder den QR-Code tippen, öffnet sich die Seite auf Smartphone oder Computer. Für Gäste ist keine separate App nötig.",
  },
  {
    q: "Wie teile ich meine Einladung?",
    a: "In der App können Sie den teilbaren Link kopieren oder den QR-Code speichern und per Nachricht, E-Mail oder Social Media senden.",
  },
  {
    q: "Was steht auf der Einladungsseite?",
    a: "In der Regel die Namen des Paares, Datum und Uhrzeit der Hochzeit, Ort mit Kartenlink, ein kurzer Einladungstext und Ihr gewähltes Bild. Mit Pro-Funktionen (z. B. Ablauf, Menüwahl, erweiterte RSVP) erscheinen diese Angaben ebenfalls auf derselben Seite.",
  },
  {
    q: "Kann ich Datum, Uhrzeit oder Ort später ändern?",
    a: "Ja. Wenn Sie die Angaben in der App aktualisieren, wird die Einladungsseite mit den neuen Informationen veröffentlicht. Ihr geteilter Link bleibt meist gleich — Sie müssen keine neue Adresse schicken; bei wichtigen Änderungen sollten Sie Gäste aber kurz informieren.",
  },
  {
    q: "Was ist RSVP, und was macht der Gast?",
    a: "RSVP bedeutet „Bitte antworten“. Der Gast sagt zu oder ab und gibt – falls gefragt – an, mit wie vielen Personen er kommt. Nach dem Absenden wird die Antwort gespeichert; Sie sehen sie in Ihrer Liste.",
  },
  {
    q: "Wo sehe ich die Gästeliste und Antworten?",
    a: "Die Antworten finden Sie im Verwaltungsbereich Ihrer Einladung in der App.",
  },
  {
    q: "Gibt es Wedy.Me nur fürs iPhone?",
    a: "Einladungen erstellen und bearbeiten geht derzeit mit der Wedy.Me-iOS‑App (iPhone). Die veröffentlichte Einladungsseite können Gäste mit jedem Gerät öffnen.",
  },
  {
    q: "Sind meine Daten und die Angaben der Gäste sicher?",
    a: "Die Verbindung läuft über sicheres HTTPS. Die Angaben der Gäste werden für die Zu‑ oder Absage zu Ihrer Feier verwendet; Einzelheiten stehen auf unserer Seite zur Datenschutzerklärung.",
  },
  {
    q: "Wer kann meinen Einladungslink sehen?",
    a: "Jede Person mit Link oder QR-Code kann die Einladungsseite öffnen. Teilen Sie den Link nur mit eingeladenen Personen und veröffentlichen Sie keine sensiblen Details oder den Link breit öffentlich.",
  },
  {
    q: "Was ist im kostenlosen Plan enthalten, was bietet Pro?",
    a: "Im kostenlosen Plan sind meist eine begrenzte Anzahl Einladungen und eine einfache Einladungsseite enthalten. Pro kann u. a. unbegrenzte (oder höhere Limits) Einladungen, RSVP ein/aus, Gästeliste, Zu‑ oder Absagen sowie Sofortbenachrichtigungen bieten — wie in der App aktuell angeboten.",
  },
  {
    q: "Wie kündige ich mein Abonnement?",
    a: "Abonnements verwalten Sie im App Store: Einstellungen > Apple‑ID > Abonnements, um Ihr Wedy.Me‑Abonnement zu kündigen oder die Verlängerung zu deaktivieren. Zahlungen und Erstattungen richten sich nach den Regeln von Apple.",
  },
]);

const fr = fb("Questions fréquentes", [
  {
    q: "Qu’est-ce que Wedy.Me ?",
    a: "Wedy.Me vous permet de créer votre faire-part de mariage sur le téléphone et de le partager avec un seul lien et un code QR. Les invités ouvrent l’invitation dans le navigateur ; vous suivez les réponses au même endroit.",
  },
  {
    q: "Les invités doivent-ils télécharger une application ?",
    a: "Non. En touchant le lien ou le code QR, la page s’ouvre sur téléphone ou ordinateur. Aucune application distincte n’est nécessaire pour les invités.",
  },
  {
    q: "Comment partager mon invitation ?",
    a: "Depuis l’application, vous pouvez copier le lien partageable ou enregistrer le code QR et l’envoyer par message, e-mail ou réseaux sociaux.",
  },
  {
    q: "Que trouve-t-on sur la page d’invitation ?",
    a: "En général les noms des mariés, la date et l’heure du mariage, le lieu avec lien vers la carte, un court texte d’invitation et l’image choisie. Avec les fonctionnalités Pro (par exemple le programme, le choix du repas, RSVP avancé), elles apparaissent sur la même page.",
  },
  {
    q: "Puis-je modifier la date, l’heure ou le lieu plus tard ?",
    a: "Oui. Quand vous mettez à jour les informations dans l’application, la page est publiée avec les nouvelles données. Le lien partagé reste en général le même ; pensez toutefois à prévenir les invités en cas de changement important.",
  },
  {
    q: "Qu’est-ce que le RSVP et que fait l’invité ?",
    a: "RSVP signifie « merci de répondre ». L’invité indique s’il vient et, si vous le demandez, combien de personnes l’accompagnent. Après envoi, la réponse est enregistrée et vous pouvez la consulter dans votre liste.",
  },
  {
    q: "Où voir la liste des invités et les réponses ?",
    a: "Vous consultez les réponses dans la zone de gestion de votre invitation, dans l’application.",
  },
  {
    q: "Wedy.Me est-il disponible uniquement sur iPhone ?",
    a: "La création et la modification se font aujourd’hui avec l’application iOS (iPhone) Wedy.Me. La page d’invitation publiée est accessible depuis n’importe quel appareil.",
  },
  {
    q: "Mes données et celles des invités sont-elles protégées ?",
    a: "Les connexions utilisent HTTPS. Les informations fournies par les invités servent à enregistrer leur réponse pour votre événement ; voir notre page Politique de confidentialité pour les détails.",
  },
  {
    q: "Qui peut accéder à mon lien d’invitation ?",
    a: "Toute personne qui possède le lien ou le QR code peut ouvrir la page. Partagez-le seulement avec les personnes invitées et évitez de diffuser des informations très sensibles ou le lien publiquement.",
  },
  {
    q: "Qu’inclut l’offre gratuite et qu’apporte Pro ?",
    a: "L’offre gratuite comprend en général un nombre limité d’invitations et une page simple. Pro peut ajouter invitations illimitées (ou plafond plus élevé), activation/désactivation du RSVP, liste d’invités, accepter/refuser et notifications instantanées — selon l’offre actuelle dans l’app.",
  },
  {
    q: "Comment résilier mon abonnement ?",
    a: "Les abonnements se gèrent dans l’App Store : Réglages > identifiant Apple > Abonnements pour annuler ou désactiver le renouvellement de Wedy.Me. Paiements et remboursements suivent les règles d’Apple.",
  },
]);

const es = fb("Preguntas frecuentes", [
  {
    q: "¿Qué es Wedy.Me?",
    a: "Wedy.Me te permite crear tu invitación de boda en el teléfono y compartirla con un solo enlace y código QR. Los invitados abren la invitación en el navegador; tú sigues las confirmaciones en un solo lugar.",
  },
  {
    q: "¿Los invitados tienen que descargar una app?",
    a: "No. Al tocar el enlace o el código QR, la página se abre en el teléfono o la computadora. Los invitados no necesitan instalar otra aplicación.",
  },
  {
    q: "¿Cómo comparto mi invitación?",
    a: "Desde la app puedes copiar el enlace para compartir o guardar el código QR y enviarlo por mensaje, correo o redes sociales.",
  },
  {
    q: "¿Qué aparece en la página de la invitación?",
    a: "Por lo general los nombres de los novios, fecha y hora del evento, lugar con enlace al mapa, un mensaje corto y la imagen que elegiste. Con funciones Pro (por ejemplo programa, preferencia de menú o RSVP avanzado), también se muestra en la misma página.",
  },
  {
    q: "¿Puedo cambiar fecha, hora o lugar después?",
    a: "Sí. Al actualizar los datos en la app, la página se publica con la nueva información. El enlace compartido suele ser el mismo; aun así conviene avisar a los invitados si hay cambios importantes.",
  },
  {
    q: "¿Qué es RSVP y qué hace el invitado?",
    a: "RSVP significa «por favor confirma». El invitado indica si asiste y, si lo pides, cuántas personas van. Al enviar el formulario, la respuesta queda guardada y puedes verla en tu lista.",
  },
  {
    q: "¿Dónde veo la lista y las respuestas?",
    a: "Puedes ver las respuestas en el área de administración de tu invitación dentro de la app.",
  },
  {
    q: "¿Wedy.Me solo está en iPhone?",
    a: "Crear y editar la invitación hoy usa la app iOS (iPhone) de Wedy.Me. La página publicada la pueden abrir los invitados desde cualquier dispositivo.",
  },
  {
    q: "¿Mis datos y los de los invitados están seguros?",
    a: "Las conexiones usan HTTPS seguro. Lo que envían los invitados sirve para registrar su respuesta a tu evento; los detalles están en nuestra Política de privacidad.",
  },
  {
    q: "¿Quién puede ver mi enlace de invitación?",
    a: "Cualquiera con el enlace o el QR puede abrir la página. Compártelo solo con quien quieras invitar; evita publicar datos muy sensibles o el enlace abierto a todo el público.",
  },
  {
    q: "¿Qué incluye el plan gratis y qué agrega Pro?",
    a: "El plan gratis suele incluir un número limitado de invitaciones y una página básica. Pro puede sumar invitaciones ilimitadas (o mayor límite), activar/desactivar RSVP, lista de invitados, aceptar/rechazar y notificaciones al instante, según la app.",
  },
  {
    q: "¿Cómo cancelo mi suscripción?",
    a: "Las suscripciones se gestionan en la App Store: Ajustes > ID de Apple > Suscripciones para cancelar o desactivar la renovación de Wedy.Me. Pagos y reembolsos siguen las reglas de Apple.",
  },
]);

const it = fb("Domande frequenti", [
  {
    q: "Che cos’è Wedy.Me?",
    a: "Wedy.Me ti permette di creare l’invito di nozze sul telefono e condividerlo con un solo link e codice QR. Gli invitati aprono l’invito nel browser; tu segui le risposte in un unico posto.",
  },
  {
    q: "Gli invitati devono scaricare un’app?",
    a: "No. Toccando il link o il QR la pagina si apre su telefono o computer. Non serve un’app separata per gli invitati.",
  },
  {
    q: "Come condivido il mio invito?",
    a: "Dall’app puoi copiare il link condivisibile o salvare il QR e inviarlo via messaggio, email o social.",
  },
  {
    q: "Cosa compare sulla pagina dell’invito?",
    a: "Di solito i nomi degli sposi, data e ora, luogo con link alla mappa, un breve messaggio e l’immagine scelta. Con le funzioni Pro (programma, preferenza pasto, RSVP avanzato) compaiono nella stessa pagina.",
  },
  {
    q: "Posso cambiare data, ora o luogo in seguito?",
    a: "Sì. Aggiornando i dettagli nell’app, la pagina viene pubblicata con le nuove informazioni. Il link condiviso di solito resta uguale; comunque avvisa gli invitati se ci sono cambi importanti.",
  },
  {
    q: "Cos’è RSVP e cosa fa l’invitato?",
    a: "RSVP significa «rispondere per cortesia». L’invitato dice se partecipa e, se chiedi, quante persone porta. Dopo l’invio la risposta è salvata e la vedi in elenco.",
  },
  {
    q: "Dove vedo lista e risposte?",
    a: "Le risposte sono nell’area di gestione del tuo invito, nell’app.",
  },
  {
    q: "Wedy.Me è disponibile solo su iPhone?",
    a: "Creare e modificare l’invito avviene oggi con l’app iOS (iPhone) Wedy.Me. La pagina pubblicata la aprono gli invitati da qualsiasi dispositivo.",
  },
  {
    q: "I miei dati e quelli degli invitati sono al sicuro?",
    a: "Le connessioni usano HTTPS. I dati degli invitati servono a registrare la risposta al tuo evento; i dettagli sono nella nostra Informativa sulla privacy.",
  },
  {
    q: "Chi può aprire il link del mio invito?",
    a: "Chi ha link o QR può aprire la pagina. Condividilo solo con chi vuoi invitare; evita informazioni molto sensibili o di pubblicare il link ovunque.",
  },
  {
    q: "Cosa include il piano gratuito e cosa aggiunge Pro?",
    a: "Il piano gratuito di solito offre un numero limitato di inviti e una pagina base. Pro può aggiungere inviti illimitati (o limite più alto), RSVP on/off, lista invitati, approva/rifiuta e notifiche immediate, come nell’app.",
  },
  {
    q: "Come annullo l’abbonamento?",
    a: "Gli abbonamenti si gestiscono nell’App Store: Impostazioni > ID Apple > Abbonamenti per annullare o disattivare il rinnovo di Wedy.Me. Pagamenti e rimborsi seguono le regole di Apple.",
  },
]);

const ptPT = fb("Perguntas frequentes", [
  {
    q: "O que é o Wedy.Me?",
    a: "O Wedy.Me permite-lhe criar o seu convite de casamento no telemóvel e partilhá-lo com uma única ligação e código QR. Os convidados abrem-no no navegador; acompanha as respostas num só lugar.",
  },
  {
    q: "Os convidados precisam de instalar uma aplicação?",
    a: "Não. Ao tocar na ligação ou no QR, a página abre no telemóvel ou no computador. Não é necessária uma aplicação separada para os convidados.",
  },
  {
    q: "Como partilho o meu convite?",
    a: "Na aplicação pode copiar a ligação partilhável ou guardar o QR e enviar por mensagem, correio eletrónico ou redes sociais.",
  },
  {
    q: "O que aparece na página do convite?",
    a: "Em geral os nomes dos noivos, data e hora, local com ligação ao mapa, uma mensagem curta e a imagem escolhida. Com funcionalidades Pro (cronograma, preferência de refeição, RSVP avançado) também aparece na mesma página.",
  },
  {
    q: "Posso alterar data, hora ou local mais tarde?",
    a: "Sim. Ao atualizar na aplicação, a página é publicada com a nova informação. A ligação costuma manter-se; ainda assim avise os convidados sobre alterações importantes.",
  },
  {
    q: "O que é RSVP e o que faz o convidado?",
    a: "O RSVP pede confirmação de presença. O convidado indica se vem e, se pedir, quantas pessoas. Após enviar, a resposta fica guardada e vê-a na lista.",
  },
  {
    q: "Onde vejo a lista e as respostas?",
    a: "Vê as respostas na área de gestão do convite na aplicação.",
  },
  {
    q: "O Wedy.Me só funciona no iPhone?",
    a: "Criar e editar o convite utiliza hoje a aplicação iOS (iPhone) Wedy.Me. A página publicada pode ser aberta em qualquer dispositivo.",
  },
  {
    q: "Os meus dados e os dos convidados estão seguros?",
    a: "As ligações usam HTTPS. As informações dos convidados servem para registar a resposta ao seu evento; detalhes na nossa Política de privacidade.",
  },
  {
    q: "Quem pode aceder à ligação do meu convite?",
    a: "Quem tiver a ligação ou o QR pode abrir a página. Partilhe só com quem deseja convidar; evite dados muito sensíveis ou divulgar a ligação publicamente.",
  },
  {
    q: "O que inclui o plano gratuito e o que a Pro acrescenta?",
    a: "O gratuito costuma incluir um número limitado de convites e uma página básica. A Pro pode incluir convites ilimitados (ou limite maior), ativar/desativar RSVP, lista de convidados, aprovar/recusar e notificações imediatas, conforme a aplicação.",
  },
  {
    q: "Como cancelo a subscrição?",
    a: "As subscrições são geridas na App Store: Definições > ID Apple > Subscrições para cancelar ou desativar a renovação do Wedy.Me. Pagamentos e reembolsos seguem as regras da Apple.",
  },
]);

const ru = fb("Часто задаваемые вопросы", [
  {
    q: "Что такое Wedy.Me?",
    a: "Wedy.Me помогает создать свадебное приглашение на телефоне и поделиться одной ссылкой и QR-кодом. Гости открывают приглашение в браузере; вы отслеживаете ответы в одном месте.",
  },
  {
    q: "Нужно ли гостям устанавливать приложение?",
    a: "Нет. По ссылке или QR-коду страница открывается на телефоне или компьютере. Отдельное приложение для гостей не требуется.",
  },
  {
    q: "Как поделиться приглашением?",
    a: "В приложении можно скопировать ссылку или сохранить QR и отправить в сообщении, по почте или в соцсетях.",
  },
  {
    q: "Что показывается на странице приглашения?",
    a: "Обычно имена молодожёнов, дата и время, место со ссылкой на карту, короткий текст и выбранное изображение. В Pro (например программа, предпочтения по еде, расширенный RSVP) это тоже на той же странице.",
  },
  {
    q: "Можно ли позже изменить дату, время или место?",
    a: "Да. После обновления данных в приложении страница публикуется заново. Ссылка чаще всего остаётся прежней; о важных изменениях всё же стоит напомнить гостям.",
  },
  {
    q: "Что такое RSVP и что делает гость?",
    a: "RSVP — ответ на приглашение. Гость сообщает, придёт ли и, если нужно, сколько человек. После отправки ответ сохраняется, вы видите его в списке.",
  },
  {
    q: "Где посмотреть список гостей и ответы?",
    a: "Ответы доступны в разделе управления вашим приглашением в приложении.",
  },
  {
    q: "Wedy.Me только для iPhone?",
    a: "Создавать и редактировать приглашение сейчас можно в iOS‑приложении Wedy.Me (iPhone). Опубликованную страницу гости открывают с любого устройства.",
  },
  {
    q: "Безопасны ли мои данные и данные гостей?",
    a: "Соединение идёт по защищённому HTTPS. Сведения от гостей используются для фиксации ответа на ваше мероприятие; подробности — на странице политики конфиденциальности.",
  },
  {
    q: "Кто может открыть ссылку на приглашение?",
    a: "Страницу откроет любой, у кого есть ссылка или QR. Делитесь только с приглашёнными; не публикуйте лишние конфиденциальные данные и ссылку всем подряд.",
  },
  {
    q: "Что в бесплатном плане, что даёт Pro?",
    a: "Бесплатно обычно доступно ограниченное число приглашений и базовая страница. Pro может добавить безлимит (или больший лимит), вкл/выкл RSVP, список гостей, подтверждение/отказ и мгновенные уведомления — как в текущей версии приложения.",
  },
  {
    q: "Как отменить подписку?",
    a: "Подписки управляются в App Store: Настройки > Apple ID > Подписки — там можно отменить или отключить продление Wedy.Me. Оплаты и возвраты по правилам Apple.",
  },
]);

const ar = fb("الأسئلة الشائعة", [
  {
    q: "ما هو Wedy.Me؟",
    a: "يتيح لك Wedy.Me إنشاء دعوة زفافك على الهاتف ومشاركتها برابط واحد ورمز QR. يفتح الضيوف الدعوة في المتصفح، وتتابعون الردود من مكان واحد.",
  },
  {
    q: "هل يجب على الضيوف تنزيل تطبيق؟",
    a: "لا. عند النقر على الرابط أو رمز QR تفتح الصفحة على الهاتف أو الحاسوب. لا حاجة لتطبيق منفصل للضيوف.",
  },
  {
    q: "كيف أشارك دعوتي؟",
    a: "من التطبيق يمكنك نسخ الرابط القابل للمشاركة أو حفظ رمز QR وإرساله عبر رسالة أو بريد أو وسائل التواصل.",
  },
  {
    q: "ما الذي يظهر في صفحة الدعوة؟",
    a: "عادة أسماء العروسين، التاريخ والوقت، المكان مع رابط الخريطة، نص قصير، والصورة التي اخترتها. مع ميزات Pro (مثل البرنامج، تفضيل الوجبة، RSVP متقدم) تظهر أيضًا في الصفحة نفسها.",
  },
  {
    q: "هل يمكنني تغيير التاريخ أو الوقت أو المكان لاحقًا؟",
    a: "نعم. عند التحديث في التطبيق تُنشر الصفحة بالمعلومات الجديدة. يبقى الرابط عادة كما هو؛ يفضّل إبلاغ الضيوف عند تغييرات مهمة.",
  },
  {
    q: "ما هو RSVP وماذا يفعل الضيف؟",
    a: "RSVP يعني «يرجى الرد». يخبرك الضيف بحضوره وعدد المرافقين إن طلبت ذلك. بعد الإرسال يُحفظ الرد ويمكنك متابعته من القائمة.",
  },
  {
    q: "أين أرى قائمة الضيوف والردود؟",
    a: "تجد الردود في منطقة إدارة دعوتك داخل التطبيق.",
  },
  {
    q: "هل Wedy.Me متاح فقط على iPhone؟",
    a: "إنشاء الدعوة وتعديلها حاليًا عبر تطبيق Wedy.Me على iOS (iPhone). الصفحة المنشورة يفتحها الضيوف من أي جهاز.",
  },
  {
    q: "هل بياناتي وبيانات الضيوف آمنة؟",
    a: "الاتصال عبر HTTPS الآمن. تُستخدم معلومات الضيوف لتسجيل الرد على مناسبتكم؛ للتفاصيل راجع صفحة سياسة الخصوصية.",
  },
  {
    q: "من يمكنه فتح رابط دعوتي؟",
    a: "كل من يملك الرابط أو رمز QR يمكنه فتح الصفحة. شارك الرابط فقط مع من تريد دعوتهم وتجنب نشر معلومات حساسة أو الرابط علنًا.",
  },
  {
    q: "ماذا يشمل المجاني وماذا يضيف Pro؟",
    a: "المجاني عادة يتضمن عددًا محدودًا من الدعوات وصفحة أساسية. قد يضيف Pro دعوات غير محدودة (أو حد أعلى)، تفعيل/إيقاف RSVP، قائمة ضيوف، قبول/رفض وإشعارات فورية — حسب العرض الحالي في التطبيق.",
  },
  {
    q: "كيف ألغي الاشتراك؟",
    a: "تُدار الاشتراكات من App Store: الإعدادات > معرف Apple > الاشتراكات لإلغاء Wedy.Me أو إيقاف التجديد. المدفوعات والاسترداد وفق قواعد Apple.",
  },
]);

const ja = fb("よくある質問", [
  {
    q: "Wedy.Meとは？",
    a: "Wedy.Meは、スマートフォンでウェディング招待ページを作り、ひとつのリンクとQRコードで共有できます。ゲストはブラウザで開き、返信はひとつの場所で確認できます。",
  },
  {
    q: "ゲストはアプリのインストールが必要ですか？",
    a: "いいえ。リンクまたはQRをタップすると、スマートフォンやパソコンでページが開きます。ゲスト用の別アプリは不要です。",
  },
  {
    q: "招待を共有するには？",
    a: "アプリから共有可能なリンクをコピーしたり、QRを保存してメッセージ・メール・SNSで送れます。",
  },
  {
    q: "招待ページには何が表示されますか？",
    a: "おふたりのお名前、日時、地図へのリンク付きの会場、短いメッセージ、選んだ画像などです。Pro機能（例：タイムライン、食事の希望、高度なRSVP）も同じページに表示されます。",
  },
  {
    q: "日時や会場はあとから変更できますか？",
    a: "はい。アプリで更新すると新しい内容が公開されます。共有リンクは通常そのままですが、重要な変更があればゲストに一言伝えると安心です。",
  },
  {
    q: "RSVPとは？ゲストは何をしますか？",
    a: "RSVPは出欠の返信です。参加可否や、必要なら同行人数を伝えます。送信後に回答が記録され、リストで確認できます。",
  },
  {
    q: "ゲストリストと回答はどこで見られますか？",
    a: "アプリ内の招待の管理エリアで確認できます。",
  },
  {
    q: "Wedy.MeはiPhoneだけですか？",
    a: "作成・編集は現時点でWedy.MeのiOS（iPhone）アプリです。公開されたページはゲストがどの端末からでも開けます。",
  },
  {
    q: "データとゲスト情報は安全ですか？",
    a: "通信はHTTPSで保護されます。ゲストが入力した情報は出欠登録のために使われます。詳しくはプライバシーポリシーをご覧ください。",
  },
  {
    q: "招待リンクは誰でも開けますか？",
    a: "リンクまたはQRを知っている人なら誰でもページを開けます。招待したい人だけに共有し、敏感な情報や公開用にリンクを広めないようご注意ください。",
  },
  {
    q: "無料プランとProの違いは？",
    a: "無料では招待の数に上限がありシンプルなページが中心です。Proでは無制限（または上限拡大）、RSVPのオンオフ、ゲスト一覧、承認/辞退、即時通知などが加わります（アプリの現行提供内容に従います）。",
  },
  {
    q: "サブスクの解約は？",
    a: "App Storeで管理します：設定 > Apple ID > サブスクリプションからWedy.Meの更新停止や解約ができます。支払いと返金はAppleの規約に従います。",
  },
]);

const zhHans = fb("常见问题", [
  {
    q: "Wedy.Me 是什么？",
    a: "Wedy.Me 帮您在手机上制作婚礼邀请页，并通过一个链接和二维码分享。宾客在浏览器中打开；您可在一处查看回复。",
  },
  {
    q: "宾客需要下载应用吗？",
    a: "不需要。点击邀请链接或二维码后，页面会在手机或电脑上打开，宾客无需安装单独应用。",
  },
  {
    q: "如何分享我的邀请？",
    a: "您可在应用中复制可分享链接，或保存二维码，通过信息、邮件或社交媒体发送。",
  },
  {
    q: "邀请页上会有什么内容？",
    a: "通常包括新人姓名、婚礼日期与时间、带地图链接的场地、简短邀请语和您选择的图片。若使用 Pro 功能（如流程安排、餐饮偏好、高级 RSVP），也会显示在同一页面。",
  },
  {
    q: "以后可以改日期、时间或场地吗？",
    a: "可以。在应用中更新信息后，页面会发布新内容。分享链接通常不变；若有重要变更，建议再通知宾客。",
  },
  {
    q: "什么是 RSVP，宾客要做什么？",
    a: "RSVP 指回复是否出席。宾客告知是否参加，若您询问则填写同行人数。提交后回复会保存，您可在列表中查看。",
  },
  {
    q: "在哪里查看宾客名单和回复？",
    a: "在应用内该邀请的管理区域查看回复。",
  },
  {
    q: "Wedy.Me 只能在 iPhone 上用吗？",
    a: "创建和编辑邀请目前使用 Wedy.Me 的 iOS（iPhone）应用。已发布的页面宾客可用任意设备打开。",
  },
  {
    q: "我的数据和宾客信息安全吗？",
    a: "连接使用安全的 HTTPS。宾客提供的信息用于登记对您活动的回复；详情请参阅隐私政策页面。",
  },
  {
    q: "谁可以打开我的邀请链接？",
    a: "拥有链接或二维码的任何人都能打开页面。请只分享给希望邀请的人，避免公开敏感信息或四处张贴链接。",
  },
  {
    q: "免费版包含什么，Pro 增加什么？",
    a: "免费版通常有邀请数量限制和基础页面。Pro 可包含无限（或更高上限）邀请、开关 RSVP、宾客列表、同意/拒绝与即时通知等，以应用当前提供为准。",
  },
  {
    q: "如何取消订阅？",
    a: "订阅在 App Store 管理：设置 > Apple ID > 订阅，可取消或关闭 Wedy.Me 的自动续费。付款与退款遵循 Apple 规则。",
  },
]);

const hi = fb("अक्सर पूछे जाने वाले प्रश्न", [
  {
    q: "Wedy.Me क्या है?",
    a: "Wedy.Me आपको फ़ोन पर शादी का न्योता बनाकर एक लिंक और QR कोड से साझा करने देता है। मेहमान ब्राउज़र में खोलते हैं; आप जवाब एक ही जगह देखते हैं।",
  },
  {
    q: "क्या मेहमानों को ऐप डाउनलोड करना होगा?",
    a: "नहीं। लिंक या QR पर टैप करने पर पेज फ़ोन या कंप्यूटर पर खुलता है। मेहमानों के लिए अलग ऐप ज़रूरी नहीं है।",
  },
  {
    q: "मैं न्योता कैसे साझा करूँ?",
    a: "ऐप से शेयर किया जा सकने वाला लिंक कॉपी करें या QR सेव करके संदेश, ईमेल या सोशल मीडिया पर भेजें।",
  },
  {
    q: "न्योते के पेज पर क्या दिखता है?",
    a: "आम तौर पर नवदंपति के नाम, तारीख और समय, मानचित्र लिंक वाला स्थान, छोटा संदेश और आपकी चुनी तस्वीर। Pro सुविधाओं (जैसे कार्यक्रम, भोजन विकल्प, उन्नत RSVP) में भी वही पेज पर दिखता है।",
  },
  {
    q: "क्या मैं बाद में तारीख, समय या स्थान बदल सकता/सकती हूँ?",
    a: "हाँ। ऐप में अपडेट करने पर पेज नई जानकारी के साथ प्रकाशित होता है। साझा लिंक अक्सर वही रहता है; महत्वपूर्ण बदलाव पर मेहमानों को संदेश भेजना अच्छा है।",
  },
  {
    q: "RSVP क्या है, मेहमान क्या करता है?",
    a: "RSVP में मेहमान बताता है कि आएगा या नहीं और ज़रूरत हो तो कितने साथ हैं। भेजने के बाद जवाब सुरक्षित रहता है, सूची में देखें।",
  },
  {
    q: "मेहमान सूची और जवाब कहाँ देखें?",
    a: "ऐप में आपके न्योते के प्रबंधन क्षेत्र में।",
  },
  {
    q: "क्या Wedy.Me सिर्फ iPhone पर है?",
    a: "बनाना और संपादन अभी Wedy.Me iOS (iPhone) ऐप से होता है। प्रकाशित पेज मेहमान किसी भी डिवाइस से खोल सकते हैं।",
  },
  {
    q: "क्या मेरा डेटा और मेहमान जानकारी सुरक्षित है?",
    a: "कनेक्शन सुरक्षित HTTPS पर है। मेहमान जानकारी आपके आयोजन के लिए जवाब दर्ज करने हेतु है; विवरण गोपनीयता नीति पृष्ठ पर।",
  },
  {
    q: "मेरे न्योते का लिंक कौन खोल सकता है?",
    a: "जिसके पास लिंक या QR हो, वह पेज खोल सकता है। केवल आमंत्रित लोगों से साझा करें; संवेदनशील जानकारी सार्वजनिक न करें।",
  },
  {
    q: "मुफ़्त प्लान में क्या है, Pro क्या जोड़ता है?",
    a: "मुफ़्त में आम तौर पर सीमित न्योते और बुनियादी पेज। Pro में असीमित (या अधिक सीमा), RSVP चालू/बंद, सूची, स्वीकृति/अस्वीकृति और तुरंत सूचनाएँ हो सकती हैं—ऐप के अनुसार।",
  },
  {
    q: "सदस्यता कैसे रद्द करूँ?",
    a: "सदस्यताएँ App Store में प्रबंधित होती हैं: सेटिंग्स > Apple ID > सब्सक्रिप्शन से Wedy.Me रद्द या नवीनीकरण बंद करें। भुगतान व रिफ़ंड Apple के नियमों से।",
  },
]);

const id = fb("Pertanyaan yang sering diajukan", [
  {
    q: "Apa itu Wedy.Me?",
    a: "Wedy.Me memungkinkan Anda membuat undangan pernikahan di ponsel dan membagikannya dengan satu tautan dan kode QR. Tamu membuka di peramban; Anda melacak balasan di satu tempat.",
  },
  {
    q: "Apakah tamu harus mengunduh aplikasi?",
    a: "Tidak. Ketika mereka mengetuk tautan atau QR, halaman terbuka di ponsel atau komputer. Tamu tidak perlu aplikasi terpisah.",
  },
  {
    q: "Bagaimana cara membagikan undangan saya?",
    a: "Dari aplikasi Anda bisa menyalin tautan yang bisa dibagikan atau menyimpan QR dan mengirim via pesan, email, atau media sosial.",
  },
  {
    q: "Apa yang tampil di halaman undangan?",
    a: "Biasanya nama pengantin, tanggal dan waktu, lokasi dengan tautan peta, pesan singkat, dan gambar pilihan Anda. Fitur Pro (misalnya jadwal, preferensi makan, RSVP lanjutan) juga di halaman yang sama.",
  },
  {
    q: "Bisakah saya mengubah tanggal, waktu, atau lokasi nanti?",
    a: "Ya. Saat memperbarui di aplikasi, halaman dipublikasikan dengan info baru. Tautan yang dibagikan biasanya sama; tetap beri tahu tamu jika ada perubahan penting.",
  },
  {
    q: "Apa itu RSVP, dan apa yang dilakukan tamu?",
    a: "RSVP berarti membalas apakah hadir. Tamu memberi tahu kehadiran dan jika diminta berapa orang yang ikut. Setelah dikirim, balasan tersimpan dan Anda lihat di daftar.",
  },
  {
    q: "Di mana saya melihat daftar tamu dan balasan?",
    a: "Di area kelola undangan Anda dalam aplikasi.",
  },
  {
    q: "Apakah Wedy.Me hanya untuk iPhone?",
    a: "Membuat dan mengedit saat ini memakai aplikasi iOS (iPhone) Wedy.Me. Halaman yang dipublikasikan bisa dibuka tamu dari perangkat apa pun.",
  },
  {
    q: "Apakah data saya dan informasi tamu aman?",
    a: "Koneksi memakai HTTPS yang aman. Informasi tamu dipakai untuk mencatat balasan acara Anda; lihat halaman Kebijakan Privasi untuk detail.",
  },
  {
    q: "Siapa yang bisa membuka tautan undangan saya?",
    a: "Siapa pun yang punya tautan atau QR dapat membuka halaman. Bagikan hanya kepada yang ingin Anda undang; hindari membagikan data sensitif atau tautan secara terbuka.",
  },
  {
    q: "Apa yang termasuk paket gratis, apa tambahan Pro?",
    a: "Gratis biasanya mencakup jumlah undangan terbatas dan halaman dasar. Pro dapat menambah undangan tak terbatas (atau batas lebih tinggi), RSVP aktif/nonaktif, daftar tamu, setujui/tolak, dan notifikasi instan—sesuai aplikasi.",
  },
  {
    q: "Bagaimana membatalkan langganan?",
    a: "Langganan dikelola di App Store: Pengaturan > ID > Langganan untuk membatalkan atau mematikan perpanjangan Wedy.Me. Pembayaran dan pengembalian mengikuti aturan Apple.",
  },
]);

const nl = fb("Veelgestelde vragen", [
  {
    q: "Wat is Wedy.Me?",
    a: "Met Wedy.Me maak je je trouwuitnodiging op je telefoon en deel je die met één link en QR-code. Gasten openen in de browser; jij ziet antwoorden op één plek.",
  },
  {
    q: "Moeten gasten een app downloaden?",
    a: "Nee. Via de link of QR opent de pagina op telefoon of computer. Geen aparte app voor gasten nodig.",
  },
  {
    q: "Hoe deel ik mijn uitnodiging?",
    a: "In de app kopieer je de deelbare link of bewaar je de QR en stuur je hem via bericht, e-mail of social media.",
  },
  {
    q: "Wat staat er op de uitnodigingspagina?",
    a: "Meestal de namen, datum en tijd, locatie met kaartlink, korte tekst en je gekozen afbeelding. Met Pro (programma, maaltijdvoorkeur, uitgebreide RSVP) staat dat op dezelfde pagina.",
  },
  {
    q: "Kan ik datum, tijd of locatie later wijzigen?",
    a: "Ja. Na bijwerken in de app wordt de pagina met nieuwe info gepubliceerd. Je gedeelde link blijft meestal hetzelfde; meld belangrijke wijzigingen wel aan gasten.",
  },
  {
    q: "Wat is RSVP en wat doet een gast?",
    a: "RSVP is een reactie op de uitnodiging. De gast geeft aan of hij/zij komt en eventueel met hoeveel personen. Na verzenden staat het antwoord in je lijst.",
  },
  {
    q: "Waar zie ik de gastenlijst en antwoorden?",
    a: "In het beheergedeelte van je uitnodiging in de app.",
  },
  {
    q: "Is Wedy.Me alleen voor iPhone?",
    a: "Maken en bewerken gebeurt nu met de Wedy.Me iOS-app (iPhone). De gepubliceerde pagina openen gasten op elk apparaat.",
  },
  {
    q: "Zijn mijn gegevens en die van gasten veilig?",
    a: "Verbindingen lopen via beveiligde HTTPS. Gegevens van gasten dienen om hun reactie vast te leggen; zie onze privacyverklaring.",
  },
  {
    q: "Wie kan mijn uitnodigingslink openen?",
    a: "Iedereen met link of QR kan de pagina openen. Deel alleen met wie je wilt uitnodigen; vermijd gevoelige info of openbare postings.",
  },
  {
    q: "Wat zit er in gratis, wat voegt Pro toe?",
    a: "Gratis heeft meestal een beperkt aantal uitnodigingen en een basis pagina. Pro kan onbeperkte (of hogere limiet) uitnodigingen, RSVP aan/uit, gastenlijst, goedkeuren/weigeren en directe meldingen toevoegen volgens de app.",
  },
  {
    q: "Hoe zeg ik mijn abonnement op?",
    a: "Abonnementen beheer je in de App Store: Instellingen > Apple ID > Abonnementen om Wedy.Me te annuleren of verlenging uit te zetten. Betalingen en terugbetaling volgen Apple.",
  },
]);

const da = fb("Ofte stillede spørgsmål", [
  {
    q: "Hvad er Wedy.Me?",
    a: "Wedy.Me lader dig lave din bryllupsinvitation på telefonen og dele den med ét link og en QR-kode. Gæster åbner i browseren; du følger svar ét sted.",
  },
  {
    q: "Skal gæster downloade en app?",
    a: "Nej. Når de trykker på linket eller QR-koden, åbner siden på telefon eller computer. Gæster behøver ikke en separat app.",
  },
  {
    q: "Hvordan deler jeg min invitation?",
    a: "I appen kan du kopiere det delingslink eller gemme QR-koden og sende den via besked, e-mail eller sociale medier.",
  },
  {
    q: "Hvad vises på invitationssiden?",
    a: "Typisk parrets navne, dato og tid, sted med kortlink, en kort tekst og dit valgte billede. Med Pro-funktioner (program, måltidspræferencer, avanceret RSVP) vises det også på samme side.",
  },
  {
    q: "Kan jeg ændre dato, tid eller sted senere?",
    a: "Ja. Når du opdaterer i appen, udgives siden med ny information. Dit delte link er som regel det samme; giv alligevel gæsterne besked ved vigtige ændringer.",
  },
  {
    q: "Hvad er RSVP, og hvad gør en gæst?",
    a: "RSVP er et svar på invitationen. Gæsten angiver om de kommer og eventuelt hvor mange personer. Efter afsendelse gemmes svaret, og du kan se det på listen.",
  },
  {
    q: "Hvor ser jeg gæsteliste og svar?",
    a: "I administrationsområdet for din invitation i appen.",
  },
  {
    q: "Er Wedy.Me kun til iPhone?",
    a: "At oprette og redigere sker i øjeblikket i Wedy.Me iOS-appen (iPhone). Den publicerede side kan gæster åbne fra enhver enhed.",
  },
  {
    q: "Er mine data og gæsteoplysninger sikre?",
    a: "Forbindelser bruger sikker HTTPS. Oplysninger fra gæster bruges til at registrere deres svar til dit arrangement; se vores privatlivspolitik.",
  },
  {
    q: "Hvem kan åbne mit invitationslink?",
    a: "Alle med linket eller QR-koden kan åbne siden. Del kun med dem du vil invitere; undgå meget følsomme oplysninger eller offentlig deling.",
  },
  {
    q: "Hvad er inkluderet i gratis, og hvad tilføjer Pro?",
    a: "Gratis har ofte et begrænset antal invitationer og en grundside. Pro kan tilføje ubegrænset (eller højere loft), RSVP til/fra, gæsteliste, godkend/afvis og øjeblikkelige meddelelser—som i den aktuelle app.",
  },
  {
    q: "Hvordan opsiger jeg abonnementet?",
    a: "Abonnementer håndteres i App Store: Indstillinger > Apple ID > Abonnementer for at annullere eller slå fornyelse fra for Wedy.Me. Betalinger og refusion følger Apples regler.",
  },
]);

const he = fb("שאלות נפוצות", [
  {
    q: "מה זה Wedy.Me?",
    a: "Wedy.Me מאפשר ליצור את הזמנת החתונה בטלפון ולשתף אותה בקישור אחד ובקוד QR. האורחים פותחים בדפדפן; אתם עוקבים אחר התשובות במקום אחד.",
  },
  {
    q: "האם האורחים צריכים להוריד אפליקציה?",
    a: "לא. בלחיצה על הקישור או ה‑QR הדף נפתח בטלפון או במחשב. אין צורך באפליקציה נפרדת לאורחים.",
  },
  {
    q: "איך משתפים את ההזמנה?",
    a: "באפליקציה ניתן להעתיק את הקישור לשיתוף או לשמור את ה‑QR ולשלוח בהודעה, במייל או ברשתות חברתיות.",
  },
  {
    q: "מה מוצג בדף ההזמנה?",
    a: "בדרך כלל שמות הזוג, תאריך ושעה, מקום עם קישור למפה, טקסט קצר והתמונה שבחרתם. ביכולות Pro (לו״ז, העדפות ארוחה, RSVP מתקדם) זה מוצג באותו דף.",
  },
  {
    q: "אפשר לשנות תאריך, שעה או מקום אחר כך?",
    a: "כן. עדכון באפליקציה מפרסם את הדף עם המידע החדש. הקישור המשותף בדרך כלל נשאר; עדיין כדאי לעדכן אורחים בשינויים חשובים.",
  },
  {
    q: "מה זה RSVP ומה האורח עושה?",
    a: "RSVP הוא מענה להזמנה. האורח מציין אם מגיע ומספר אנשים אם ביקשתם. אחרי השליחה התשובה נשמרת וניתן לראות אותה ברשימה.",
  },
  {
    q: "איפה רואים רשימת אורחים ותשובות?",
    a: "באזור הניהול של ההזמנה באפליקציה.",
  },
  {
    q: "האם Wedy.Me זמין רק ב‑iPhone?",
    a: "יצירה ועריכה מתבצעות כיום באפליקציית Wedy.Me ל‑iOS (iPhone). את הדף המפורסם אורחים יכולים לפתוח מכל מכשיר.",
  },
  {
    q: "האם הנתונים והמידע על האורחים מוגנים?",
    a: "החיבורים משתמשים ב‑HTTPS מאובטח. מידע מהאורחים משמש לרישום התשובה לאירוע; פרטים במדיניות הפרטיות.",
  },
  {
    q: "מי יכול לפתוח את קישור ההזמנה?",
    a: "כל מי שיש לו את הקישור או ה‑QR יכול לפתוח את הדף. שתפו רק עם מי שברצונכם להזמין; הימנעו מפרטים רגישים או פרסום ציבורי.",
  },
  {
    q: "מה כלול בחינם ומה Pro מוסיף?",
    a: "בחינם בדרך כלל מספר מוגבל של הזמנות ודף בסיסי. Pro יכול להוסיף ללא הגבלה (או תקרה גבוהה יותר), הפעלה/כיבוי RSVP, רשימת אורחים, אישור/דחייה והתראות מיידיות—לפי האפליקציה.",
  },
  {
    q: "איך מבטלים מנוי?",
    a: "מנויים מנוהלים ב‑App Store: הגדרות > Apple ID > מנויים כדי לבטל או לכבות חידוש עבור Wedy.Me. תשלומים והחזרים לפי כללי Apple.",
  },
]);

const no = fb("Ofte stilte spørsmål", [
  {
    q: "Hva er Wedy.Me?",
    a: "Wedy.Me lar deg lage bryllupsinvitasjonen på telefonen og dele den med én lenke og QR-kode. Gjestene åpner i nettleseren; du følger svar ett sted.",
  },
  {
    q: "Må gjester laste ned en app?",
    a: "Nei. Når de trykker på lenken eller QR-koden, åpnes siden på telefon eller datamaskin. Gjestene trenger ikke en egen app.",
  },
  {
    q: "Hvordan deler jeg invitasjonen?",
    a: "I appen kan du kopiere delingslenken eller lagre QR-koden og sende den via melding, e-post eller sosiale medier.",
  },
  {
    q: "Hva vises på invitasjonssiden?",
    a: "Vanligvis parets navn, dato og tid, sted med kartlenke, kort tekst og bildet du valgte. Med Pro-funksjoner (program, måltidspreferanser, avansert RSVP) vises det også på samme side.",
  },
  {
    q: "Kan jeg endre dato, tid eller sted senere?",
    a: "Ja. Når du oppdaterer i appen, publiseres siden med ny informasjon. Den delte lenken er vanligvis den samme; si fra til gjester ved viktige endringer.",
  },
  {
    q: "Hva er RSVP, og hva gjør en gjest?",
    a: "RSVP er svar på invitasjonen. Gjesten sier om de kommer og eventuelt hvor mange personer. Etter innsending lagres svaret og du ser det i listen.",
  },
  {
    q: "Hvor ser jeg gjesteliste og svar?",
    a: "I administrasjonsområdet for invitasjonen i appen.",
  },
  {
    q: "Er Wedy.Me bare for iPhone?",
    a: "Å opprette og redigere skjer nå i Wedy.Me iOS-appen (iPhone). Den publiserte siden kan gjester åpne fra hvilken som helst enhet.",
  },
  {
    q: "Er dataene og gjesteinformasjonen trygge?",
    a: "Tilkoblinger bruker sikker HTTPS. Informasjon fra gjester brukes til å registrere svaret til arrangementet ditt; se personvernerklæringen.",
  },
  {
    q: "Hvem kan åpne invitasjonslenken min?",
    a: "Alle med lenken eller QR-koden kan åpne siden. Del bare med dem du vil invitere; unngå svært sensitive opplysninger eller offentlig deling.",
  },
  {
    q: "Hva er inkludert i gratis, og hva legger Pro til?",
    a: "Gratis har ofte et begrenset antall invitasjoner og en enkel side. Pro kan legge til ubegrenset (eller høyere grense), RSVP på/av, gjesteliste, godkjenn/avvis og øyeblikkelige varsler—som i appen.",
  },
  {
    q: "Hvordan sier jeg opp abonnementet?",
    a: "Abonnementer håndteres i App Store: Innstillinger > Apple ID > Abonnementer for å kansellere eller slå av fornyelse for Wedy.Me. Betalinger og refusjon følger Apples regler.",
  },
]);

const sk = fb("Často kladené otázky", [
  {
    q: "Čo je Wedy.Me?",
    a: "Wedy.Me vám umožní vytvoriť svadobné pozvanie v telefóne a zdieľať ho jedným odkazom a QR kódom. Hostia otvárajú v prehliadači; odpovede sledujete na jednom mieste.",
  },
  {
    q: "Musia hostia sťahovať aplikáciu?",
    a: "Nie. Po klepnutí na odkaz alebo QR sa stránka otvára v telefóne alebo počítači. Hostia nepotrebujú samostatnú aplikáciu.",
  },
  {
    q: "Ako zdieľam pozvánku?",
    a: "V aplikácii môžete skopírovať zdieľateľný odkaz alebo uložiť QR a poslať správou, e-mailom alebo na sociálnych sieťach.",
  },
  {
    q: "Čo sa zobrazuje na stránke pozvánky?",
    a: "Zvyčajne mená páru, dátum a čas, miesto s odkazom na mapu, krátky text a váš obrázok. S funkciami Pro (program, preferencie stravy, rozšírené RSVP) sa to zobrazuje na tej istej stránke.",
  },
  {
    q: "Môžem neskôr zmeniť dátum, čas alebo miesto?",
    a: "Áno. Po aktualizácii v aplikácii sa stránka publikuje s novými informáciami. Zdieľaný odkaz zostáva zvyčajne rovnaký; pri dôležitých zmenách hostí informujte.",
  },
  {
    q: "Čo je RSVP a čo robí hosť?",
    a: "RSVP je odpoveď na pozvánku. Hosť uvádza, či príde a prípadne koľko osôb. Po odoslaní sa odpoveď uloží a vidíte ju v zozname.",
  },
  {
    q: "Kde vidím zoznam hostí a odpovede?",
    a: "V správe pozvánky v aplikácii.",
  },
  {
    q: "Je Wedy.Me len pre iPhone?",
    a: "Vytváranie a úpravy prebiehajú v aplikácii Wedy.Me pre iOS (iPhone). Publikovanú stránku môžu hostia otvoriť na akomkoľvek zariadení.",
  },
  {
    q: "Sú moje údaje a údaje hostí v bezpečí?",
    a: "Spojenia používajú zabezpečené HTTPS. Informácie od hostí slúžia na registráciu odpovede na vašu udalosť; podrobnosti v zásadách ochrany osobných údajov.",
  },
  {
    q: "Kto môže otvoriť odkaz na moju pozvánku?",
    a: "Ktokoľvek s odkazom alebo QR kódom môže otvoriť stránku. Zdieľajte len s tými, ktorých chcete pozvať; vyhnite sa citlivým údajom alebo verejnému šíreniu.",
  },
  {
    q: "Čo obsahuje bezplatná verzia a čo pridáva Pro?",
    a: "Bezplatná verzia zvyčajne zahŕňa obmedzený počet pozvánok a základnú stránku. Pro môže pridať neobmedzené (alebo vyšší limit), zapnúť/vypnúť RSVP, zoznam hostí, schváliť/zamietnuť a okamžité upozornenia—podľa aplikácie.",
  },
  {
    q: "Ako zruším predplatné?",
    a: "Predplatné spravujete v App Store: Nastavenia > Apple ID > Predplatné na zrušenie alebo vypnutie obnovenia Wedy.Me. Platby a vrátenia podľa pravidiel Apple.",
  },
]);

const uk = fb("Часті запитання", [
  {
    q: "Що таке Wedy.Me?",
    a: "Wedy.Me дозволяє створити весільне запрошення на телефоні та поділитися ним одним посиланням і QR-кодом. Гості відкривають у браузері; ви відстежуєте відповіді в одному місці.",
  },
  {
    q: "Чи потрібно гостям завантажувати застосунок?",
    a: "Ні. Після натискання посилання або QR сторінка відкривається на телефоні або комп’ютері. Окремий застосунок для гостей не потрібен.",
  },
  {
    q: "Як поділитися запрошенням?",
    a: "У застосунку можна скопіювати посилання для поширення або зберегти QR і надіслати повідомленням, електронною поштою чи в соцмережах.",
  },
  {
    q: "Що показується на сторінці запрошення?",
    a: "Зазвичай імена пари, дата й час, місце з посиланням на карту, короткий текст і обране зображення. Функції Pro (розклад, харчові побажання, розширене RSVP) також на тій самій сторінці.",
  },
  {
    q: "Чи можна змінити дату, час або місце пізніше?",
    a: "Так. Після оновлення в застосунку сторінка публікується з новою інформацією. Поширене посилання зазвичай не змінюється; про важливі зміни варто повідомити гостей.",
  },
  {
    q: "Що таке RSVP і що робить гість?",
    a: "RSVP — відповідь на запрошення. Гість повідомляє, чи прийде, і за потреби скільки людей. Після надсилання відповідь зберігається, і ви бачите її в списку.",
  },
  {
    q: "Де переглянути список гостей і відповіді?",
    a: "У розділі керування запрошенням у застосунку.",
  },
  {
    q: "Чи Wedy.Me лише для iPhone?",
    a: "Створення та редагування зараз у застосунку Wedy.Me для iOS (iPhone). Опубліковану сторінку гості можуть відкрити з будь-якого пристрою.",
  },
  {
    q: "Чи безпечні мої дані та дані гостей?",
    a: "З’єднання використовують захищений HTTPS. Інформація від гостей потрібна для реєстрації відповіді на вашу подію; деталі в політиці конфіденційності.",
  },
  {
    q: "Хто може відкрити посилання на моє запрошення?",
    a: "Будь-хто з посиланням або QR може відкрити сторінку. Діліться лише з тими, кого хочете запросити; уникайте дуже чутливих даних або публічного поширення.",
  },
  {
    q: "Що входить у безкоштовний план і що додає Pro?",
    a: "Безкоштовно зазвичай обмежена кількість запрошень і базова сторінка. Pro може додати необмежені (або вищий ліміт) запрошення, увімк./вимк. RSVP, список гостей, схвалення/відмову та миттєві сповіщення—як у застосунку.",
  },
  {
    q: "Як скасувати підписку?",
    a: "Підписки керуються в App Store: Налаштування > Apple ID > Підписки, щоб скасувати або вимкнути поновлення Wedy.Me. Платежі та повернення за правилами Apple.",
  },
]);

const vi = fb("Câu hỏi thường gặp", [
  {
    q: "Wedy.Me là gì?",
    a: "Wedy.Me giúp bạn tạo thiệp cưới trên điện thoại và chia sẻ bằng một liên kết và mã QR. Khách mở trên trình duyệt; bạn theo dõi phản hồi tại một nơi.",
  },
  {
    q: "Khách có phải tải ứng dụng không?",
    a: "Không. Chạm vào liên kết hoặc QR thì trang mở trên điện thoại hoặc máy tính. Khách không cần cài app riêng.",
  },
  {
    q: "Làm sao để chia sẻ thiệp mời?",
    a: "Trong ứng dụng bạn có thể sao chép liên kết có thể chia sẻ hoặc lưu mã QR và gửi qua tin nhắn, email hoặc mạng xã hội.",
  },
  {
    q: "Trang thiệp mời hiển thị những gì?",
    a: "Thường có tên cô dâu chú rể, ngày giờ, địa điểm có liên kết bản đồ, lời mời ngắn và hình bạn chọn. Tính năng Pro (ví dụ lịch trình, ăn kiêng, RSVP nâng cao) cũng trên cùng trang.",
  },
  {
    q: "Có đổi ngày, giờ hoặc địa điểm sau không?",
    a: "Có. Cập nhật trong ứng dụng thì trang được xuất bản với thông tin mới. Liên kết chia sẻ thường giữ nguyên; nếu thay đổi quan trọng nên báo khách.",
  },
  {
    q: "RSVP là gì, khách làm gì?",
    a: "RSVP là trả lời có tham dự hay không (và số người đi cùng nếu bạn hỏi). Sau khi gửi, câu trả lời được lưu và bạn xem trong danh sách.",
  },
  {
    q: "Xem danh sách khách và phản hồi ở đâu?",
    a: "Trong khu vực quản lý thiệp mời trong ứng dụng.",
  },
  {
    q: "Wedy.Me chỉ dùng trên iPhone?",
    a: "Tạo và chỉnh sửa hiện dùng ứng dụng iOS (iPhone) Wedy.Me. Trang đã xuất bản khách mở được trên mọi thiết bị.",
  },
  {
    q: "Dữ liệu và thông tin khách có an toàn không?",
    a: "Kết nối dùng HTTPS an toàn. Thông tin khách dùng để ghi nhận phản hồi cho sự kiện; chi tiết xem trang Chính sách quyền riêng tư.",
  },
  {
    q: "Ai có thể mở liên kết thiệp của tôi?",
    a: "Ai có liên kết hoặc QR đều mở được trang. Chỉ chia sẻ với người bạn muốn mời; tránh thông tin nhạy cảm hoặc đăng công khai.",
  },
  {
    q: "Gói miễn phí gồm gì, Pro thêm gì?",
    a: "Miễn phí thường giới hạn số thiệp và trang cơ bản. Pro có thể thêm không giới hạn (hoặc giới hạn cao hơn), bật/tắt RSVP, danh sách khách, duyệt/từ chối và thông báo tức thì—theo ứng dụng.",
  },
  {
    q: "Hủy đăng ký thế nào?",
    a: "Đăng ký quản lý trên App Store: Cài đặt > Apple ID > Đăng ký để hủy hoặc tắt gia hạn Wedy.Me. Thanh toán và hoàn tiền theo quy tắc của Apple.",
  },
]);

export const LANDING_FAQ: Record<AppLocale, FaqBundle> = {
  en,
  ar,
  "zh-Hans": zhHans,
  da,
  nl,
  fr,
  de,
  he,
  hi,
  id,
  it,
  ja,
  no,
  "pt-PT": ptPT,
  ru,
  sk,
  es,
  tr,
  uk,
  vi,
};

